import { create } from 'zustand';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { Class, ClassUnit, ClassDocumentWithDetails, ClassUnitWithDocuments, ClassWithDetails, ClassDocumentType } from '@/types';

interface ClassState {
  classes: Class[];
  currentClass: ClassWithDetails | null;
  isLoading: boolean;
  error: string | null;
  setClasses: (classes: Class[]) => void;
  setCurrentClass: (classData: ClassWithDetails | null) => void;
  fetchClasses: (userId: string) => Promise<void>;
  createClass: (userId: string, data: { name: string; description?: string; color?: string; semester?: string }) => Promise<Class | null>;
  updateClass: (classId: string, updates: Partial<Class>) => Promise<void>;
  deleteClass: (classId: string) => Promise<void>;
  archiveClass: (classId: string, archived: boolean) => Promise<void>;
  fetchClassDetails: (classId: string) => Promise<void>;
  createUnit: (classId: string, name: string, description?: string) => Promise<ClassUnit | null>;
  updateUnit: (unitId: string, updates: Partial<ClassUnit>) => Promise<void>;
  deleteUnit: (unitId: string) => Promise<void>;
  reorderUnits: (classId: string, unitIds: string[]) => Promise<void>;
  addDocuments: (classId: string, documentIds: string[], unitId?: string, documentType?: ClassDocumentType) => Promise<void>;
  removeDocument: (classId: string, documentId: string) => Promise<void>;
  moveDocument: (classDocId: string, newUnitId: string | null) => Promise<void>;
  updateDocumentType: (classDocId: string, documentType: ClassDocumentType) => Promise<void>;
}

export const useClassStore = create<ClassState>((set) => ({
  classes: [],
  currentClass: null,
  isLoading: false,
  error: null,

  setClasses: (classes) => set({ classes }),
  setCurrentClass: (currentClass) => set({ currentClass }),

  fetchClasses: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('classes')
      .select('*')
      .eq('user_id', userId)
      .eq('archived', false)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchClasses');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ classes: data || [], isLoading: false });
  },

  createClass: async (userId: string, data: { name: string; description?: string; color?: string; semester?: string }) => {
    const newClass = {
      user_id: userId,
      name: data.name,
      description: data.description,
      color: data.color || 'blue',
      semester: data.semester,
      archived: false,
    };

    const { data: result, error } = await supabase
      .from('classes')
      .insert(newClass)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'classStore.createClass');
      set({ error: error.message });
      return null;
    }

    set((state) => ({
      classes: [result, ...state.classes],
    }));

    return result;
  },

  updateClass: async (classId: string, updates: Partial<Class>) => {
    const { error } = await supabase
      .from('classes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', classId);

    if (error) {
      reportSupabaseError(error, 'classStore.updateClass');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      classes: state.classes.map((c) =>
        c.id === classId ? { ...c, ...updates } : c
      ),
      currentClass:
        state.currentClass?.id === classId
          ? { ...state.currentClass, ...updates }
          : state.currentClass,
    }));
  },

  deleteClass: async (classId: string) => {
    const { error } = await supabase
      .from('classes')
      .delete()
      .eq('id', classId);

    if (error) {
      reportSupabaseError(error, 'classStore.deleteClass');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      classes: state.classes.filter((c) => c.id !== classId),
      currentClass:
        state.currentClass?.id === classId ? null : state.currentClass,
    }));
  },

  archiveClass: async (classId: string, archived: boolean) => {
    const { error } = await supabase
      .from('classes')
      .update({ archived, updated_at: new Date().toISOString() })
      .eq('id', classId);

    if (error) {
      reportSupabaseError(error, 'classStore.archiveClass');
      set({ error: error.message });
      return;
    }

    // Remove from active list if archived
    if (archived) {
      set((state) => ({
        classes: state.classes.filter((c) => c.id !== classId),
        currentClass:
          state.currentClass?.id === classId ? null : state.currentClass,
      }));
    }
  },

  fetchClassDetails: async (classId: string) => {
    set({ isLoading: true, error: null });

    // Fetch class
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('*')
      .eq('id', classId)
      .single();

    if (classError) {
      reportSupabaseError(classError, 'fetchClassDetails.class');
      set({ error: classError.message, isLoading: false });
      return;
    }

    // Fetch units
    const { data: units, error: unitsError } = await supabase
      .from('class_units')
      .select('*')
      .eq('class_id', classId)
      .order('order_index', { ascending: true });

    if (unitsError) {
      reportSupabaseError(unitsError, 'fetchClassDetails.units');
      set({ error: unitsError.message, isLoading: false });
      return;
    }

    // Fetch class documents with document details
    const { data: classDocuments, error: docsError } = await supabase
      .from('class_documents')
      .select(`
        *,
        document:documents(*)
      `)
      .eq('class_id', classId)
      .order('order_index', { ascending: true });

    if (docsError) {
      reportSupabaseError(docsError, 'fetchClassDetails.documents');
      set({ error: docsError.message, isLoading: false });
      return;
    }

    // Transform documents to include document details and map file_type to type
    const transformedDocs: ClassDocumentWithDetails[] = (classDocuments || []).map((cd) => ({
      ...cd,
      document: cd.document ? {
        ...cd.document,
        type: cd.document.file_type,
      } : null,
    })).filter((cd) => cd.document !== null) as ClassDocumentWithDetails[];

    // Organize into units with documents and unassigned documents
    const unitsWithDocuments: ClassUnitWithDocuments[] = (units || []).map((unit) => ({
      ...unit,
      documents: transformedDocs.filter((cd) => cd.unit_id === unit.id),
    }));

    const unassignedDocuments = transformedDocs.filter((cd) => !cd.unit_id);

    const classWithDetails: ClassWithDetails = {
      ...classData,
      units: unitsWithDocuments,
      unassigned_documents: unassignedDocuments,
    };

    set({ currentClass: classWithDetails, isLoading: false });
  },

  createUnit: async (classId: string, name: string, description?: string) => {
    // Get current max order_index
    const { data: existingUnits } = await supabase
      .from('class_units')
      .select('order_index')
      .eq('class_id', classId)
      .order('order_index', { ascending: false })
      .limit(1);

    const maxOrderIndex = existingUnits?.[0]?.order_index ?? -1;

    const newUnit = {
      class_id: classId,
      name,
      description,
      order_index: maxOrderIndex + 1,
    };

    const { data, error } = await supabase
      .from('class_units')
      .insert(newUnit)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'classStore.createUnit');
      set({ error: error.message });
      return null;
    }

    // Add to current class details
    set((state) => {
      if (!state.currentClass || state.currentClass.id !== classId) return state;
      return {
        currentClass: {
          ...state.currentClass,
          units: [...state.currentClass.units, { ...data, documents: [] }],
        },
      };
    });

    return data;
  },

  updateUnit: async (unitId: string, updates: Partial<ClassUnit>) => {
    const { error } = await supabase
      .from('class_units')
      .update(updates)
      .eq('id', unitId);

    if (error) {
      reportSupabaseError(error, 'classStore.updateUnit');
      set({ error: error.message });
      return;
    }

    set((state) => {
      if (!state.currentClass) return state;
      return {
        currentClass: {
          ...state.currentClass,
          units: state.currentClass.units.map((u) =>
            u.id === unitId ? { ...u, ...updates } : u
          ),
        },
      };
    });
  },

  deleteUnit: async (unitId: string) => {
    // Documents in this unit will have unit_id set to NULL due to ON DELETE SET NULL
    const { error } = await supabase
      .from('class_units')
      .delete()
      .eq('id', unitId);

    if (error) {
      reportSupabaseError(error, 'classStore.deleteUnit');
      set({ error: error.message });
      return;
    }

    set((state) => {
      if (!state.currentClass) return state;

      // Find the unit being deleted
      const deletedUnit = state.currentClass.units.find((u) => u.id === unitId);

      // Move documents from deleted unit to unassigned
      const movedDocs = deletedUnit?.documents.map((d) => ({ ...d, unit_id: undefined })) || [];

      return {
        currentClass: {
          ...state.currentClass,
          units: state.currentClass.units.filter((u) => u.id !== unitId),
          unassigned_documents: [...state.currentClass.unassigned_documents, ...movedDocs],
        },
      };
    });
  },

  reorderUnits: async (classId: string, unitIds: string[]) => {
    // Update order_index for each unit
    const updates = unitIds.map((id, index) =>
      supabase
        .from('class_units')
        .update({ order_index: index })
        .eq('id', id)
    );

    await Promise.all(updates);

    set((state) => {
      if (!state.currentClass || state.currentClass.id !== classId) return state;

      // Reorder units based on unitIds
      const reorderedUnits = unitIds
        .map((id) => state.currentClass!.units.find((u) => u.id === id))
        .filter((u): u is ClassUnitWithDocuments => u !== undefined)
        .map((u, index) => ({ ...u, order_index: index }));

      return {
        currentClass: {
          ...state.currentClass,
          units: reorderedUnits,
        },
      };
    });
  },

  addDocuments: async (classId: string, documentIds: string[], unitId?: string, documentType: ClassDocumentType = 'material') => {
    // Get current max order_index for the target (class or unit)
    const query = supabase
      .from('class_documents')
      .select('order_index')
      .eq('class_id', classId);

    if (unitId) {
      query.eq('unit_id', unitId);
    } else {
      query.is('unit_id', null);
    }

    const { data: existingDocs } = await query
      .order('order_index', { ascending: false })
      .limit(1);

    const maxOrderIndex = existingDocs?.[0]?.order_index ?? -1;

    // Insert new class_documents
    const newDocs = documentIds.map((docId, index) => ({
      class_id: classId,
      document_id: docId,
      unit_id: unitId || null,
      document_type: documentType,
      order_index: maxOrderIndex + 1 + index,
    }));

    const { data, error } = await supabase
      .from('class_documents')
      .insert(newDocs)
      .select(`
        *,
        document:documents(*)
      `);

    if (error) {
      reportSupabaseError(error, 'classStore.addDocuments');
      set({ error: error.message });
      return;
    }

    // Transform and add to state
    const transformedDocs: ClassDocumentWithDetails[] = (data || []).map((cd) => ({
      ...cd,
      document: cd.document ? {
        ...cd.document,
        type: cd.document.file_type,
      } : null,
    })).filter((cd) => cd.document !== null) as ClassDocumentWithDetails[];

    set((state) => {
      if (!state.currentClass || state.currentClass.id !== classId) return state;

      if (unitId) {
        // Add to specific unit
        return {
          currentClass: {
            ...state.currentClass,
            units: state.currentClass.units.map((u) =>
              u.id === unitId
                ? { ...u, documents: [...u.documents, ...transformedDocs] }
                : u
            ),
          },
        };
      } else {
        // Add to unassigned
        return {
          currentClass: {
            ...state.currentClass,
            unassigned_documents: [...state.currentClass.unassigned_documents, ...transformedDocs],
          },
        };
      }
    });
  },

  removeDocument: async (classId: string, documentId: string) => {
    const { error } = await supabase
      .from('class_documents')
      .delete()
      .eq('class_id', classId)
      .eq('document_id', documentId);

    if (error) {
      reportSupabaseError(error, 'classStore.removeDocument');
      set({ error: error.message });
      return;
    }

    set((state) => {
      if (!state.currentClass || state.currentClass.id !== classId) return state;

      return {
        currentClass: {
          ...state.currentClass,
          units: state.currentClass.units.map((u) => ({
            ...u,
            documents: u.documents.filter((d) => d.document_id !== documentId),
          })),
          unassigned_documents: state.currentClass.unassigned_documents.filter(
            (d) => d.document_id !== documentId
          ),
        },
      };
    });
  },

  moveDocument: async (classDocId: string, newUnitId: string | null) => {
    const { error } = await supabase
      .from('class_documents')
      .update({ unit_id: newUnitId })
      .eq('id', classDocId);

    if (error) {
      reportSupabaseError(error, 'classStore.moveDocument');
      set({ error: error.message });
      return;
    }

    set((state) => {
      if (!state.currentClass) return state;

      // Find the document in current state
      let movedDoc: ClassDocumentWithDetails | undefined;

      // Check units
      for (const unit of state.currentClass.units) {
        const doc = unit.documents.find((d) => d.id === classDocId);
        if (doc) {
          movedDoc = { ...doc, unit_id: newUnitId || undefined };
          break;
        }
      }

      // Check unassigned
      if (!movedDoc) {
        const doc = state.currentClass.unassigned_documents.find((d) => d.id === classDocId);
        if (doc) {
          movedDoc = { ...doc, unit_id: newUnitId || undefined };
        }
      }

      if (!movedDoc) return state;

      // Remove from old location and add to new
      const newUnits = state.currentClass.units.map((u) => ({
        ...u,
        documents: u.documents.filter((d) => d.id !== classDocId),
      }));

      let newUnassigned = state.currentClass.unassigned_documents.filter(
        (d) => d.id !== classDocId
      );

      if (newUnitId) {
        // Add to specific unit
        return {
          currentClass: {
            ...state.currentClass,
            units: newUnits.map((u) =>
              u.id === newUnitId
                ? { ...u, documents: [...u.documents, movedDoc!] }
                : u
            ),
            unassigned_documents: newUnassigned,
          },
        };
      } else {
        // Add to unassigned
        return {
          currentClass: {
            ...state.currentClass,
            units: newUnits,
            unassigned_documents: [...newUnassigned, movedDoc],
          },
        };
      }
    });
  },

  updateDocumentType: async (classDocId: string, documentType: ClassDocumentType) => {
    const { error } = await supabase
      .from('class_documents')
      .update({ document_type: documentType })
      .eq('id', classDocId);

    if (error) {
      reportSupabaseError(error, 'classStore.updateDocumentType');
      set({ error: error.message });
      return;
    }

    set((state) => {
      if (!state.currentClass) return state;

      return {
        currentClass: {
          ...state.currentClass,
          units: state.currentClass.units.map((u) => ({
            ...u,
            documents: u.documents.map((d) =>
              d.id === classDocId ? { ...d, document_type: documentType } : d
            ),
          })),
          unassigned_documents: state.currentClass.unassigned_documents.map((d) =>
            d.id === classDocId ? { ...d, document_type: documentType } : d
          ),
        },
      };
    });
  },
}));
